import type {
  PlaycastMouseButton,
  PlaycastMouseLocation,
  PlaycastMouseMode,
  PlaycastMouseState,
  PlaycastMouseWheel,
} from '@playcastdotio/CoreTypes';
import type {
  PlaycastMessage,
  PlaycastMessageMouseDown,
  PlaycastMessageMouseMove,
  PlaycastMessageMouseSetMode,
  PlaycastMessageMouseSetState,
  PlaycastMessageMouseUp,
  PlaycastMessageMouseWheel,
  PlaycastMessageSource,
} from '../types';
import { formatMessage } from './_util/formatMessage';

const target = 'mouse';

export const setMode = (hostId: string, mode: PlaycastMouseMode, userAuth?: string): PlaycastMessage<PlaycastMessageMouseSetMode> => {
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageMouseSetMode = {
    target,
    action: 'setMode',
    isReply: false,
    message: mode,
  };
  return formatMessage(hostId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageMouseSetMode>;
};

/**
 * @experimental This is an experimental feature and may be changed in the future.
 * You should use the `down` function instead.
 */
export const experimentalButtonDown = (button: PlaycastMouseButton, userAuth?: string): PlaycastMessage<PlaycastMessageMouseDown> => {
  const hostId = 'experimental';
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageMouseDown = {
    target,
    action: 'down',
    isReply: false,
    message: button,
  };
  return formatMessage(hostId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageMouseDown>;
};

/**
 * @experimental This is an experimental feature and may be changed in the future.
 * You should use the `up` function instead.
 */
export const experimentalButtonUp = (button: PlaycastMouseButton, userAuth?: string): PlaycastMessage<PlaycastMessageMouseUp> => {
  const hostId = 'experimental';
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageMouseUp = {
    target,
    action: 'up',
    isReply: false,
    message: button,
  };
  return formatMessage(hostId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageMouseUp>;
};

/**
 * @experimental This is an experimental feature and may be changed in the future.
 * You should use the `move` function instead.
 */
export const experimentalMove = (location: PlaycastMouseLocation, userAuth?: string): PlaycastMessage<PlaycastMessageMouseMove> => {
  const hostId = 'experimental';
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageMouseMove = {
    target,
    action: 'move',
    isReply: false,
    message: location,
  };
  return formatMessage(hostId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageMouseMove>;
};

/**
 * @experimental This is an experimental feature and may be changed in the future.
 * You should use the `wheel` function instead.
 */
export const experimentalWheel = (wheelEvent: PlaycastMouseWheel, userAuth?: string): PlaycastMessage<PlaycastMessageMouseWheel> => {
  const hostId = 'experimental';
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageMouseWheel = {
    target,
    action: 'wheel',
    isReply: false,
    message: wheelEvent,
  };
  return formatMessage(hostId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageMouseWheel>;
};

export const setState = (hostId: string, state: PlaycastMouseState, userAuth?: string): PlaycastMessage<PlaycastMessageMouseSetState> => {
  const messageSource: PlaycastMessageSource = 'player';
  const message: PlaycastMessageMouseSetState = {
    target,
    action: 'setState',
    isReply: false,
    message: state,
  };
  return formatMessage(hostId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageMouseSetState>;
};

export const down = (hostId: string, button: PlaycastMouseButton, userAuth?: string): PlaycastMessage<PlaycastMessageMouseDown> => {
  const messageSource: PlaycastMessageSource = 'player';
  const message: PlaycastMessageMouseDown = {
    target,
    action: 'down',
    isReply: false,
    message: button,
  };
  return formatMessage(hostId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageMouseDown>;
};

export const up = (hostId: string, button: PlaycastMouseButton, userAuth?: string): PlaycastMessage<PlaycastMessageMouseUp> => {
  const messageSource: PlaycastMessageSource = 'player';
  const message: PlaycastMessageMouseUp = {
    target,
    action: 'up',
    isReply: false,
    message: button,
  };
  return formatMessage(hostId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageMouseUp>;
};

export const move = (hostId: string, location: PlaycastMouseLocation, userAuth?: string): PlaycastMessage<PlaycastMessageMouseMove> => {
  const messageSource: PlaycastMessageSource = 'player';
  const message: PlaycastMessageMouseMove = {
    target,
    action: 'move',
    isReply: false,
    message: location,
  };
  return formatMessage(hostId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageMouseMove>;
};

export const wheel = (hostId: string, wheelEvent: PlaycastMouseWheel, userAuth?: string): PlaycastMessage<PlaycastMessageMouseWheel> => {
  const messageSource: PlaycastMessageSource = 'player';
  const message: PlaycastMessageMouseWheel = {
    target,
    action: 'wheel',
    isReply: false,
    message: wheelEvent,
  };
  return formatMessage(hostId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageMouseWheel>;
};
